package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.data.repository.SentenceResult
import com.shadowings.gero.data.repository.DatabaseRepositoryImpl
import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.presentation.model.Word
import kotlin.random.Random

fun buildAdverbialSentenceSync(
    soggettiCount: Int = 1,
    withParticleIw: Boolean = false,
    random: Random = Random.Default,
    words: List<Word>,
    adverbialSentences: List<SentenceOptions> = DatabaseRepositoryImpl.adverbialSentences,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    subjectHavePossessive: Boolean = false,
    possessivePairs: List<PossessivePairs> = DatabaseRepositoryImpl.possessivePairs
): SentenceResult {
    require(soggettiCount >= 1) { "soggettiCount deve essere >= 1" }

    fun candidatesOf(rows: List<SentenceOptions>) = rows
        .map { Pair(it.subjects, it.places) }
        .filter { (subjects, places) -> subjects.size >= soggettiCount && places.isNotEmpty() }

    // With subjectHavePossessive only subjects that can take one are drawn. If no row has one,
    // the sentence just goes without instead of failing.
    val ownedCandidates = if (subjectHavePossessive && !thisSubject) {
        candidatesOf(withPossessableSubjects(adverbialSentences, words, possessivePairs))
    } else {
        emptyList()
    }
    val withPossessive = ownedCandidates.isNotEmpty()
    val candidates = if (withPossessive) ownedCandidates else candidatesOf(adverbialSentences)

    if (candidates.isEmpty()) {
        error("Nessuna riga in sentences_adverbial.tsv soddisfa i criteri: soggetti=$soggettiCount")
    }

    val chosenPattern = candidates[random.nextInt(candidates.size)]
    val (patternSubjects, patternPlaces) = chosenPattern

    val shuffledSubjects = patternSubjects.shuffled(random)
    var chosenSubjectsKeys = pickDistinctIdsByBaseWord(shuffledSubjects, soggettiCount, words)

    if (chosenSubjectsKeys.size < soggettiCount) {
        chosenSubjectsKeys = shuffledSubjects.take(soggettiCount)
    }

    val chosenPlaceKey = patternPlaces[random.nextInt(patternPlaces.size)]

    val chosenSubjects = chosenSubjectsKeys.map { id ->
        words.find { it.id == id } ?: error("Word subject con id $id non trovato")
    }
    val chosenPlace = words.find { it.id == chosenPlaceKey }
        ?: error("Word place con id $chosenPlaceKey non trovato")
    // Drawn only when asked for, so the random stream of every other caller stays the same.
    val subjectPossessive = if (withPossessive) {
        pickPossessivePronoun(chosenSubjects.first(), words, possessivePairs, random)
    } else {
        null
    }

    return buildAdverbialSentenceFromWords(
        subjects = chosenSubjects,
        place = chosenPlace,
        withParticleIw = withParticleIw,
        isNegative = isNegative,
        thisSubject = thisSubject,
        subjectPossessive = subjectPossessive
    )
}

fun buildAdverbialSentenceFromWords(
    subjects: List<Word>,
    place: Word,
    withParticleIw: Boolean = false,
    isNegative: Boolean = false,
    thisSubject: Boolean = false,
    // Suffix pronoun owning the first subject (sꜣ.i "mio figlio"). Not with the demonstrative.
    subjectPossessive: Word? = null
): SentenceResult {
    require(subjects.isNotEmpty()) { "subjects non può essere vuoto" }
    val possessive = subjectPossessive?.takeUnless { thisSubject }

    // 1) Hieroglyphs
    val subjectHieros = subjects.mapIndexed { index, word ->
        if (index == 0 && thisSubject) {
            "${word.gardiner}-${getDemonstrativeGardiner(word)}"
        } else if (index == 0 && possessive != null) {
            "${word.gardiner}-${possessive.gardiner}"
        } else {
            word.gardiner
        }
    }.joinToString("-")
    val placeHiero = place.gardiner
    val prepHiero = "G17"
    val baseHieroglyphs = "$subjectHieros-$prepHiero-$placeHiero"
    val hieroglyphs = if (isNegative) "D35:N35-$baseHieroglyphs" else if (withParticleIw) "M17-G43-$baseHieroglyphs" else baseHieroglyphs

    // 2) Transliteration
    val subjectTrans = subjects.mapIndexed { index, word ->
        if (index == 0 && thisSubject) {
            "${word.reading} ${getDemonstrativeReading(word)}"
        } else if (index == 0 && possessive != null) {
            "${word.reading}.${possessive.reading}"
        } else {
            word.reading
        }
    }.joinToString(" ")
    val placeTrans = place.reading
    val baseTransliteration = "$subjectTrans m $placeTrans"
    val transliteration = if (isNegative) "nn $baseTransliteration" else if (withParticleIw) "iw $baseTransliteration" else baseTransliteration

    // Plural checks
    val isSubjectPlural = subjects.size > 1 || subjects.any { it.isPlural }

    // 3) Italian Translation
    val subjectStringsIt = subjects.mapIndexed { index, it ->
        if (index == 0 && thisSubject) formatItalianDemonstrativeSubject(it)
        else if (index == 0 && possessive != null) possessiveTranslation(it, possessive.id, "it")
        else formatItalianSubject(it)
    }
    val joinedSubjectsIt = joinListIt(subjectStringsIt)
    val verbIt = if (isNegative) {
        if (isSubjectPlural) "non sono" else "non è"
    } else {
        if (isSubjectPlural) "sono" else "è"
    }
    val placeIt = formatItalianInPlace(place)
    val translationIt = "$joinedSubjectsIt $verbIt $placeIt"

    // 4) English Translation
    val subjectStringsEn = subjects.mapIndexed { index, it ->
        if (index == 0 && thisSubject) formatEnglishDemonstrativeSubject(it)
        else if (index == 0 && possessive != null) possessiveTranslation(it, possessive.id, "en")
        else formatEnglishSubject(it)
    }
    val joinedSubjectsEn = joinListEn(subjectStringsEn)
    val verbEn = if (isNegative) {
        if (isSubjectPlural) "are not" else "is not"
    } else {
        if (isSubjectPlural) "are" else "is"
    }
    val placeEn = formatEnglishInPlace(place)
    val translationEn = "$joinedSubjectsEn $verbEn $placeEn"

    // 5) German Translation
    val subjectStringsDe = subjects.mapIndexed { index, it ->
        if (index == 0 && thisSubject) formatGermanDemonstrativeSubject(it)
        else if (index == 0 && possessive != null) possessiveTranslation(it, possessive.id, "de")
        else formatGermanSubject(it)
    }
    val joinedSubjectsDe = joinListDe(subjectStringsDe)
    val verbDe = if (isSubjectPlural) "sind" else "ist"
    val placeDe = formatGermanInPlace(place)
    val translationDe = if (isNegative) "$joinedSubjectsDe $verbDe nicht $placeDe" else "$joinedSubjectsDe $verbDe $placeDe"

    // 6) Spanish Translation
    val subjectStringsEs = subjects.mapIndexed { index, it ->
        if (index == 0 && thisSubject) formatSpanishDemonstrativeSubject(it)
        else if (index == 0 && possessive != null) possessiveTranslation(it, possessive.id, "es")
        else formatSpanishSubject(it)
    }
    val joinedSubjectsEs = joinListEs(subjectStringsEs)
    val verbEs = if (isNegative) {
        if (isSubjectPlural) "no están" else "no está"
    } else {
        if (isSubjectPlural) "están" else "está"
    }
    val placeEs = formatSpanishInPlace(place)
    val translationEs = "$joinedSubjectsEs $verbEs $placeEs"

    // 7) French Translation
    val subjectStringsFr = subjects.mapIndexed { index, it ->
        if (index == 0 && thisSubject) formatFrenchDemonstrativeSubject(it)
        else if (index == 0 && possessive != null) possessiveTranslation(it, possessive.id, "fr")
        else formatFrenchSubject(it)
    }
    val joinedSubjectsFr = joinListFr(subjectStringsFr)
    val verbFr = if (isNegative) {
        if (isSubjectPlural) "ne sont pas" else "n'est pas"
    } else {
        if (isSubjectPlural) "sont" else "est"
    }
    val placeFr = formatFrenchInPlace(place)
    val translationFr = "$joinedSubjectsFr $verbFr $placeFr"

    return SentenceResult(
        hieroglyphs = hieroglyphs,
        transliteration = transliteration,
        translationIt = translationIt,
        translationEn = translationEn,
        translationDe = translationDe,
        translationEs = translationEs,
        translationFr = translationFr,
        isNegative = isNegative,
        thisSubject = thisSubject
    )
}

fun buildAdverbialTextTranslationQuizzes(
    random: Random = Random.Default,
    words: List<Word>,
    languageCode: String,
    count: Int = 7,
    withParticleIw: Boolean = false,
    adverbialSentences: List<SentenceOptions> = DatabaseRepositoryImpl.adverbialSentences
): List<TextTranslationContent> {
    val quizzes = mutableListOf<TextTranslationContent>()
    var attempts = 0
    while (quizzes.size < count && attempts < count * 20) {
        attempts++
        val soggettiCount = if (random.nextBoolean()) 1 else 2
        val useIw = if (withParticleIw) true else (random.nextFloat() < 0.5f)
        val sentenceResult = buildAdverbialSentenceSync(
            soggettiCount = soggettiCount,
            withParticleIw = useIw,
            random = random,
            words = words,
            adverbialSentences = adverbialSentences
        )
        if (quizzes.any { it.hieroglyphs == sentenceResult.hieroglyphs }) {
            continue
        }
        val translation = when (languageCode) {
            "it" -> sentenceResult.translationIt
            "de" -> sentenceResult.translationDe
            "es" -> sentenceResult.translationEs
            "fr" -> sentenceResult.translationFr
            else -> sentenceResult.translationEn
        }
        quizzes.add(
            TextTranslationContent(
                hieroglyphs = sentenceResult.hieroglyphs,
                correctPhrase = translation,
                transliteration = sentenceResult.transliteration,
                soggettiCount = soggettiCount
            )
        )
    }
    return quizzes
}

fun buildAdverbialPhoneticKeyboardQuizzes(
    random: Random = Random.Default,
    words: List<Word>,
    languageCode: String,
    count: Int = 3,
    withParticleIw: Boolean = false,
    adverbialSentences: List<SentenceOptions> = DatabaseRepositoryImpl.adverbialSentences
): List<PhoneticKeyboardContent> {
    val quizzes = mutableListOf<PhoneticKeyboardContent>()
    var attempts = 0
    while (quizzes.size < count && attempts < count * 20) {
        attempts++
        val soggettiCount = if (random.nextBoolean()) 1 else 2
        val useIw = if (withParticleIw) true else (random.nextFloat() < 0.5f)
        val sentenceResult = buildAdverbialSentenceSync(
            soggettiCount = soggettiCount,
            withParticleIw = useIw,
            random = random,
            words = words,
            adverbialSentences = adverbialSentences
        )
        if (quizzes.any { it.sentence == sentenceResult.hieroglyphs }) {
            continue
        }
        val translation = when (languageCode) {
            "it" -> sentenceResult.translationIt
            "de" -> sentenceResult.translationDe
            "es" -> sentenceResult.translationEs
            "fr" -> sentenceResult.translationFr
            else -> sentenceResult.translationEn
        }
        quizzes.add(
            PhoneticKeyboardContent(
                sentence = sentenceResult.hieroglyphs,
                info = "${sentenceResult.transliteration} -> ${translation}",
                reading = sentenceResult.transliteration.map { it.toString() },
                isProperName = false,
                canRotate = false
            )
        )
    }
    return quizzes
}

internal fun formatItalianInPlace(place: Word): String {
    val art = place.getArticolo("it")
    val meaning = getCleanMeaning(place, "it")
    if (art.isNullOrBlank() || art == "-") return "in $meaning"
    val prep = when (art) {
        "il" -> "nel"
        "lo" -> "nello"
        "l'" -> "nell'"
        "la" -> "nella"
        "i" -> "nei"
        "gli" -> "negli"
        "le" -> "nelle"
        else -> "in $art"
    }
    return if (prep.endsWith("'")) {
        "$prep$meaning"
    } else {
        "$prep $meaning"
    }
}

internal fun formatEnglishInPlace(place: Word): String {
    val art = place.getArticolo("en")
    val meaning = getCleanMeaning(place, "en")
    return if (art.isNullOrBlank() || art == "-") {
        "in $meaning"
    } else {
        "in $art $meaning"
    }
}

// `accusative`: the verb moves something into the place (words.tsv
// de_directional, "geht ins Haus"); otherwise the dative of position ("im Haus").
internal fun formatGermanInPlace(place: Word, accusative: Boolean = false): String {
    val art = place.getArticolo("de")
    val meaning = getCleanMeaning(place, "de")
    if (art.isNullOrBlank() || art == "-") return "in $meaning"
    if (accusative) {
        if (place.isPlural) return "in die $meaning"
        return when (art) {
            "der" -> "in den ${germanObliqueSingular(place, meaning)}"
            "das" -> "ins $meaning"
            "die" -> "in die $meaning"
            else -> "in $art $meaning"
        }
    }
    return if (place.isPlural) {
        val pluralNoun =
            if (meaning.endsWith("n") || meaning.endsWith("s")) meaning else "${meaning}n"
        "in den $pluralNoun"
    } else {
        when (art) {
            "der", "das" -> "im ${germanObliqueSingular(place, meaning)}"
            "die" -> "in der $meaning"
            else -> "in $art $meaning"
        }
    }
}

internal fun formatSpanishInPlace(place: Word): String {
    val art = place.getArticolo("es")
    val meaning = getCleanMeaning(place, "es")
    return if (art.isNullOrBlank() || art == "-") {
        "en $meaning"
    } else {
        "en $art $meaning"
    }
}

// French "dans" doesn't contract with the definite article (unlike "à"/"de").
internal fun formatFrenchInPlace(place: Word): String {
    val art = place.getArticolo("fr")
    val meaning = getCleanMeaning(place, "fr")
    if (art.isNullOrBlank() || art == "-") return "dans $meaning"
    return if (art.endsWith("'")) "dans $art$meaning" else "dans $art $meaning"
}
